import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { CHILEAN_BANKS, BANK_ACCOUNT_TYPES } from "@/lib/constants";
import { formatRut, isValidRut } from "@/lib/rut";
import { Landmark, ShieldCheck, AlertTriangle, Lock, Loader2 } from "lucide-react";
import HelpHint from "@/components/HelpHint";

/**
 * Sección privada de datos bancarios (Pago Protegido).
 * Sólo se renderiza para lawyer / mediator (llamada desde Profile.js).
 * El backend guarda estos datos en users.banking_info y los expone únicamente
 * vía private_user() (self + admin).
 */
export default function BankingInfoSection({ user, onUpdated }) {
  const bi = user?.banking_info || {};
  const complete = !!user?.banking_info_complete;

  const [form, setForm] = useState({
    account_holder_name: bi.account_holder_name || user?.name || "",
    account_holder_rut: bi.account_holder_rut || user?.rut || "",
    bank: bi.bank || "",
    account_type: bi.account_type || "corriente",
    account_number: bi.account_number || "",
    notification_email: bi.notification_email || user?.email || "",
  });
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    // Validaciones cliente
    if (!form.account_holder_name.trim()) return setMsg({ type: "error", text: "Ingresa el nombre del titular." });
    if (!form.account_holder_rut.trim() || !isValidRut(form.account_holder_rut)) {
      return setMsg({ type: "error", text: "RUT del titular inválido (verifica el dígito verificador)." });
    }
    if (!form.bank) return setMsg({ type: "error", text: "Selecciona un banco." });
    if (!form.account_type) return setMsg({ type: "error", text: "Selecciona el tipo de cuenta." });
    if (!form.account_number.trim()) return setMsg({ type: "error", text: "Ingresa el número de cuenta." });

    setBusy(true);
    try {
      const { data } = await api.put("/auth/banking-info", form);
      setMsg({ type: "success", text: "Datos bancarios guardados. Ya puedes recibir pagos vía Pago Protegido." });
      onUpdated?.(data.user);
    } catch (err) {
      setMsg({ type: "error", text: formatApiError(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="panel-glow p-6 mt-6" data-testid="banking-info-section">
      <div className="flex items-start gap-3">
        <Landmark size={22} className="text-emerald-400 mt-1" />
        <div className="flex-1">
          <div className="label-eyebrow text-emerald-400">Información para pagos</div>
          <div className="serif text-2xl mt-1">Datos bancarios (privados)</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl inline-flex items-start gap-2">
            <Lock size={13} className="mt-0.5 shrink-0" />
            Estos datos son privados. Sólo tú y el equipo de ColegaLink los pueden ver; se usan para transferirte los pagos de mediaciones y delegaciones cuando el servicio se completa (Pago Protegido).
          </p>
        </div>
        {complete ? (
          <span className="pill text-[10px] border-emerald-400/50 bg-emerald-400/10 text-emerald-300 inline-flex items-center gap-1">
            <ShieldCheck size={11} /> Completo
          </span>
        ) : (
          <span className="pill text-[10px] border-amber-400/50 bg-amber-400/10 text-amber-300 inline-flex items-center gap-1">
            <AlertTriangle size={11} /> Falta completar
          </span>
        )}
      </div>

      {!complete && (
        <div className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Para marcarte como <strong>Disponible</strong> o postular a delegaciones necesitas completar estos datos.
        </div>
      )}

      <form onSubmit={submit} className="mt-6 grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="label-eyebrow inline-flex items-center gap-1">Nombre del titular <HelpHint>Debe coincidir EXACTAMENTE con el nombre registrado en el banco. Si no coincide, la transferencia será rechazada.</HelpHint></span>
          <input
            type="text"
            value={form.account_holder_name}
            onChange={(e) => set("account_holder_name", e.target.value)}
            className="field mt-1.5 w-full"
            placeholder="Juan Pérez González"
            data-testid="banking-holder-name"
            required
          />
        </label>

        <label className="block">
          <span className="label-eyebrow inline-flex items-center gap-1">RUT del titular <HelpHint>RUT de la persona (o RUT empresa) dueña de la cuenta. Debe coincidir con el que registró el banco.</HelpHint></span>
          <input
            type="text"
            value={form.account_holder_rut}
            onChange={(e) => set("account_holder_rut", formatRut(e.target.value))}
            className="field mt-1.5 w-full"
            placeholder="12.345.678-9"
            data-testid="banking-holder-rut"
            required
          />
        </label>

        <label className="block">
          <span className="label-eyebrow">Banco</span>
          <select
            value={form.bank}
            onChange={(e) => set("bank", e.target.value)}
            className="field mt-1.5 w-full"
            data-testid="banking-bank"
            required
          >
            <option value="">— Selecciona —</option>
            {CHILEAN_BANKS.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="label-eyebrow">Tipo de cuenta</span>
          <select
            value={form.account_type}
            onChange={(e) => set("account_type", e.target.value)}
            className="field mt-1.5 w-full"
            data-testid="banking-account-type"
            required
          >
            {BANK_ACCOUNT_TYPES.map((t) => (
              <option key={t.code} value={t.code}>{t.label}</option>
            ))}
          </select>
        </label>

        <label className="block sm:col-span-2">
          <span className="label-eyebrow inline-flex items-center gap-1">Número de cuenta <HelpHint>Solo dígitos. NO incluyas puntos ni guiones. Ej: BancoEstado Vista/RUT tiene 8 dígitos; cta corriente varía.</HelpHint></span>
          <input
            type="text"
            value={form.account_number}
            onChange={(e) => set("account_number", e.target.value.replace(/[^\d-]/g, ""))}
            className="field mt-1.5 w-full"
            placeholder="Solo números"
            data-testid="banking-account-number"
            inputMode="numeric"
            required
          />
        </label>

        <label className="block sm:col-span-2">
          <span className="label-eyebrow">Correo electrónico para avisos de transferencia</span>
          <input
            type="email"
            value={form.notification_email}
            onChange={(e) => set("notification_email", e.target.value)}
            className="field mt-1.5 w-full"
            placeholder="pagos@tu-correo.cl (opcional)"
            data-testid="banking-notification-email"
          />
          <span className="text-[11px] text-muted-foreground mt-1 block">
            Si lo dejas vacío, se usará el correo de tu cuenta ({user?.email}).
          </span>
        </label>

        {msg.text && (
          <div
            className={`sm:col-span-2 rounded-xl px-4 py-3 text-sm ${
              msg.type === "success"
                ? "border border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
                : "border border-red-500/40 bg-red-500/10 text-red-200"
            }`}
            data-testid="banking-message"
          >
            {msg.text}
          </div>
        )}

        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={busy}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-60"
            data-testid="banking-submit"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Guardando…" : "Guardar datos bancarios"}
          </button>
        </div>
      </form>
    </div>
  );
}
